<template>
  <div>
    <!-- ヘッダー -->
    <v-row>
      <v-col cols="12">
        <h2>
          <v-icon class="mr-2"> mdi-note-text-outline </v-icon>
          請求データ
        </h2>
        <v-spacer class="mt-2" />
      </v-col> </v-row
    ><!-- /ヘッダー -->

    <!-- 検索ブロック -->
    <v-row>
      <v-col>
        <v-expansion-panels accordion>
          <v-expansion-panel>
            <v-expansion-panel-header
              class="panel-header-color panel-selectable"
            >
              <strong>
                <v-icon class="panel-header-color"> mdi-magnify </v-icon>
                &nbsp;条件を指定して検索
              </strong>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="5">
                      <v-text-field
                        v-model="searchForms.contractorName"
                        clearable
                        persistent-placeholder
                        label="法人名"
                      />
                    </v-col>
                    <v-col cols="12" md="5">
                      <v-text-field
                        v-model="searchForms.tenantName"
                        clearable
                        label="テナント名"
                        persistent-placeholder
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="2">
                      <cs-date-picker
                        :date.sync="searchForms.invoiceStartDate"
                        @dateChangedCallback="invoiceStartDateChangedCallback"
                      >
                        <template v-slot="{ on, attrs }">
                          <v-text-field
                            v-model="searchForms.invoiceStartDate"
                            v-bind="attrs"
                            v-on="on"
                            clearable
                            dense
                            hide-details="auto"
                            label="請求日開始（From）"
                            persistent-hint
                            persistent-placeholder
                            placeholder="日付を選択"
                            readonly
                            style="font-size: 90%"
                            @click:clear="searchForms.invoiceStartDate = null"
                          > 
                            <template v-slot:append-outer>
                              <div
                                v-if="$vuetify.breakpoint.mdAndUp"
                                style="vertical-align: middle"
                              >
                                〜
                              </div>
                            </template>
                          </v-text-field>
                        </template>
                      </cs-date-picker>
                    </v-col>
                    <v-col cols="12" md="2">
                      <cs-date-picker
                        :date.sync="searchForms.invoiceEndDate"
                        @dateChangedCallback="invoiceEndDateChangedCallback"
                      >
                        <template v-slot="{ on, attrs }">
                          <v-text-field
                            v-model="searchForms.invoiceEndDate"
                            v-bind="attrs"
                            v-on="on"
                            clearable
                            dense
                            hide-details="auto"
                            label="請求日終了（To）"
                            persistent-hint
                            persistent-placeholder
                            placeholder="日付を選択"
                            readonly
                            style="font-size: 90%"
                            @click:clear="searchForms.invoiceEndDate = null"
                          /> 
                        </template>
                      </cs-date-picker>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" @click="searchButtonClickEvent">
                    <v-icon> mdi-magnify </v-icon>
                    &nbsp;検索
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <!-- /検索ブロック -->

    <v-row>
      <v-col class="text-left">
        <h4>{{ total_count }} 件</h4>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card class="rounded-card">
          <v-card-text>
            <v-data-table
              :headers="tableHeaders"
              :items="invoices"
              :items-per-page="-1"
              disable-pagination
              disable-sort
              hide-default-footer
            >
              <!-- 請求日 -->
              <template v-slot:[`item.invoice_date`]="{ item }">
                {{ item.invoice_date | date_format }}
              </template>
              <!-- /請求日 -->

              <!-- 費目名 -->
              <template v-slot:[`item.expence_name`]="{ item }">
                <v-btn
                  v-if="item.csv_output"
                  text
                  color="primary"
                  small
                  style="text-transform: none"
                  @click="csvOutputButtonClickEvent(item)"
                >
                  {{ item.expence_name }}
                </v-btn>
                <v-btn v-else 
                  text 
                  small
                  style="text-transform: none">
                  {{ item.expence_name }}
                </v-btn>
              </template>
              <!-- /費目名 -->

              <!-- 単価 -->
              <template v-slot:[`item.price`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.price) | comma_format }}
                </span>
              </template>
              <!-- /単価 -->

              <!-- 数量 -->
              <template v-slot:[`item.quantity`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.quantity) | comma_format }}
                </span>
              </template>
              <!-- /数量 -->

              <!-- 請求金額 -->
              <template v-slot:[`item.invoice_amount`]="{ item }">
                <span class="font-weight-black">
                  {{ parseFloat(item.invoice_amount) | comma_format }}
                </span>
              </template>
              <!-- /請求金額 -->
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ページャ -->
    <v-row v-if="hasResult" dense>
      <v-col>
        <v-pagination
          v-model="page"
          :length="pagination.total_pages"
          :total-visible="totalVisible"
          circle
          prev-icon="mdi-menu-left"
          next-icon="mdi-menu-right"
          @input="changePage"
        />
      </v-col>
    </v-row>
    <!-- /ページャ -->
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import DownloadMixin from "../../../mixins/download-mixin";
import moment from "moment";

export default {
  name: "InvoiceIndex",
  components: {},
  mixins: [DownloadMixin],
  data() {
    return {
      page: 1,
      totalVisible: 7,
      total_count: null,
      targetId: null,
      searchForms: {
        contractorName: null,
        tenantName: null,
        invoiceStartDate: moment()
          .add(-1, "months")
          .endOf("month")
          .format("YYYY-MM-DD"),
        invoiceEndDate: null,
      },
      tableHeaders: [
        { value: "tenant_name", text: "テナント名" },
        { value: "invoice_date", text: "請求日" },
        { value: "payment_type_name", text: "支払種別" },
        { value: "expence_name", text: "費目名" },
        { value: "price", text: "単価", align: "right" },
        { value: "quantity", text: "数量", align: "right" },
        { value: "invoice_amount", text: "請求金額", align: "right" },
        { value: "slip_number", text: "Paid伝票番号" },
      ],
      invoices: [],
    };
  },
  computed: {
    ...mapState("AdminInvoice", {
      pagination: "pagination",
    }),
    ...mapGetters("AdminInvoice", ["hasResult"]),
  },
  created() {
    this.searchInvoice();
  },
  methods: {
    ...mapActions("AdminInvoice", {
      fetchInvoices: "index",
    }),
    /**
     * 請求開始日変更　コールバック
     */
    invoiceStartDateChangedCallback (value) {
      this.searchForms.invoiceStartDate = value;
    },
    /**
     * 請求終了日変更　コールバック
     */
    invoiceEndDateChangedCallback (value) {
      this.searchForms.invoiceEndDate = value;
    },
    searchButtonClickEvent() {
      this.page = 1;
      this.searchInvoice();
    },
    changePage() {
      this.searchInvoice();
    },
    /**
     * CSV出力
     * @param {*} item
     */
    csvOutputButtonClickEvent(item) {
      let params = this.buildParam("csv", item.id, item.expence_id);
      this.fetchInvoices(params).then((content) => {
        let target_month = moment(item.invoice_date).format("YYYYMM");
        this.downloadCsv(content, `${item.tenant_name}_${target_month}`);
      });
    },
    /**
     * 通常の検索
     */
    searchInvoice() {
      let params = this.buildParam("json", null, null);
      this.fetchInvoices(params).then((content) => {
        this.invoices = content.invoices;
        this.total_count = content.pagination.total_count;
      });
    },
    /*パラメータ設定 */
    buildParam(format, id, expence_id) {
      return {
        contractor_name: this.searchForms.contractorName,
        tenant_name: this.searchForms.tenantName,
        invoice_start_date: this.searchForms.invoiceStartDate,
        invoice_end_date: this.searchForms.invoiceEndDate,
        id: id,
        expence_id: expence_id,
        page: this.page,
        format: format,
      };
    },
  },
};
</script>

<style scoped>
</style>
